import { lookupsJson } from '@ourbranch/lookups';

const percentageOptions = lookupsJson.farm.windstormOrHail;
const DEFAULT_FLOOR = 1500;

/**
 * Calculate wind/hail deductible options for a Coverage F item.
 * Reuses the same logic as the FO6 Structures per-risk wind/hail deductible
 * (staff-fe/src/common/components/brand-components/forreston/farm/base/IL/deductible-wind-hail.jsx).
 *
 * @param {Object} params
 * @param {number} params.coverage - The item's coverage limit
 * @param {number} params.allPerilDeductible - The item's all-peril deductible
 * @param {number} [params.floor=1500] - Minimum calculated wind/hail deductible amount.
 *   CFM floors at $2,500 (issue #32483); other carriers use the $1,500 default.
 * @returns {{ options: Array<{id: number, value: string}>, isOverride: boolean, targetValue: number|null }}
 */
export function getWindHailOptions({ coverage, allPerilDeductible, floor = DEFAULT_FLOOR }) {
  const FLOOR = floor;
  const allPeril = Number(allPerilDeductible) || 0;

  if (!coverage) {
    const percentageIds = new Set(percentageOptions.map((o) => o.id));
    if (allPeril > 0 && !percentageIds.has(allPeril)) {
      return {
        options: [...percentageOptions, { id: allPeril, value: `Match All Peril ($${allPeril.toLocaleString()})` }],
        isOverride: false,
        targetValue: allPeril
      };
    }
    return { options: percentageOptions, isOverride: false, targetValue: null };
  }

  const dollarAmounts = percentageOptions.map(({ id }) => Math.round(coverage * id));
  const highest = dollarAmounts[dollarAmounts.length - 1];
  const lowest = dollarAmounts[0];

  // All Peril exceeds all percentage options — wind hail must match
  if (allPeril > highest && allPeril > FLOOR) {
    return {
      options: [{ id: allPeril, value: `Match All Peril ($${allPeril.toLocaleString()})` }],
      isOverride: true,
      targetValue: allPeril
    };
  }

  const opts = [];

  // Wind/Hail must be at least as high as FLOOR or All-Peril, whichever is higher
  const minimumAllowed = Math.max(FLOOR, allPeril);

  // Floor option when the lowest percentage doesn't reach the minimum
  if (lowest < minimumAllowed) {
    opts.push({ id: minimumAllowed, value: `$${minimumAllowed.toLocaleString()}` });
  }

  // Percentage options whose dollar amount meets the minimum (FLOOR or All-Peril)
  for (let i = 0; i < percentageOptions.length; i++) {
    if (dollarAmounts[i] >= minimumAllowed) {
      const { id, value } = percentageOptions[i];
      opts.push({ id, value: `${value} ($${dollarAmounts[i].toLocaleString()})` });
    }
  }

  // "Match All Peril" when it sits between percentages and isn't an exact match
  // Don't add if it's already been added as the minimum floor option
  if (allPeril > lowest && allPeril < highest && !dollarAmounts.includes(allPeril) && allPeril !== minimumAllowed) {
    opts.push({ id: allPeril, value: `Match All Peril ($${allPeril.toLocaleString()})` });
  }

  return { options: opts, isOverride: false, targetValue: opts[0]?.id ?? FLOOR };
}
